import {graphql} from 'react-apollo';

import {APPROVAL_MODE} from '../../../constants';
import {SAVE_TIME_MUTATION} from '../../queries';
import {
  applyOptimisticEntryToCache,
  applySaveTimeResultToCache,
  buildSaveTimeEntry,
  buildSaveTimeVariables,
  extractGraphQLErrorMessage,
  pendingSaveKey
} from './_saveTimeHelpers';

// saveTime mutation
// Used for:
//  - updating the time allocation on a single entry (invoked from TimesheetEntries)
//  - updating the overnight comp flag on a single entry (invoked from TimesheetDetail)
//
// Parameters are all optional:
//  - hours: new time allocation (if specified).  0 to delete the entry
//  - overnightCompensation: new overnight compensation flag value for this entry
//  - rowIndex, entryIndex: identify the entry
//
// When the user has disabled auto-save via the control bar toggle, instead of
// firing this mutation we apply the change optimistically to the Apollo cache
// and queue it in redux.  `flushPendingSaves` later commits the whole queue
// via a single saveTimeBatch GraphQL request.
export default graphql(SAVE_TIME_MUTATION, {
  props: ({
    ownProps: {
      setError,
      setSuccess,
      updateTimesheetData,
      setSavePending,
      queuePendingSave,
      timesheet,
      mode,
      autoSave
    },
    mutate
  }) => ({
    updateTime({
      hours,
      overnightCompensation,
      isGig,
      rowIndex,
      entryIndex,
      detailIndex = 0
    }) {
      if (
        hours !== undefined &&
        (typeof hours !== 'number' || Number.isNaN(hours) || !Number.isFinite(hours))
      ) {
        setError('Hours must be a valid number.');
        return Promise.resolve();
      }

      const built = buildSaveTimeEntry({
        timesheet,
        rowIndex,
        entryIndex,
        detailIndex,
        hours,
        overnightCompensation,
        isGig
      });
      if (built.skip) return Promise.resolve();
      const {row, entry} = built;

      // Auto-save OFF: queue the change for a later batched save.
      if (autoSave === false && queuePendingSave) {
        applyOptimisticEntryToCache({
          updateTimesheetData,
          rowIndex,
          entryIndex,
          detailIndex,
          entry,
          row
        });
        const key = pendingSaveKey({rowIndex, entryIndex, detailIndex});
        queuePendingSave({
          key,
          rowIndex,
          entryIndex,
          detailIndex,
          // snapshot the row/entry inputs so the batch handler doesn't need to
          // reconstruct them from a (possibly further-edited) timesheet state
          variables: buildSaveTimeVariables(timesheet, row, entry),
          entryDate: entry.Date
        });
        return Promise.resolve();
      }

      // Auto-save ON: fire the mutation immediately (existing behaviour).
      // Lock the day and cell so concurrent edits don't corrupt the Epicor
      // LaborHed header while this request is in flight.
      setSavePending({
        [entry.Date]: true,
        [entry.id]: true
      });
      return mutate({
        variables: {
          ...buildSaveTimeVariables(timesheet, row, entry),
          submit: mode === APPROVAL_MODE
        },
        optimisticResponse: {
          __typename: 'Mutation',
          saveTime: {
            __typename: 'TimesheetEntry',
            InLieuType: null,
            ...row.entries[entryIndex],
            ...entry,
            ...(isGig !== undefined ? {PayType: isGig ? 'GIG' : 'REG'} : {})
          }
        },

        update: (store, {data: {saveTime}}) => {
          applySaveTimeResultToCache({
            updateTimesheetData,
            store,
            row,
            rowIndex,
            entryIndex,
            detailIndex,
            saveTime
          });
        }
      })
        .catch(err => {
          const message = extractGraphQLErrorMessage(err);
          setError(message || err);
        })
        .then(() => setSuccess('Timesheet entry has been updated.'))
        .then(() => {
          // unlock cell and column
          setSavePending({[entry.id]: false, [entry.Date]: false});
        });
    }
  })
});
